import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/Button";

// Developer-only test bench for the notification pipeline. Sends a
// test email to the signed-in user via the same send-notification
// edge function the rest of the app uses, then surfaces the result
// in-page so you don't have to crack open Supabase logs to verify
// the Resend integration is working.
//
// When push notifications are added, this same button will also
// fire a push to the developer's registered devices.
export function NotificationTestPanel() {
  const user = useAuth((s) => s.user);
  const isDev = user?.level === "owner";
  const [status, setStatus] = useState<
    { kind: "ok"; detail: string } | { kind: "err"; detail: string } | null
  >(null);

  const testMut = useMutation({
    mutationFn: () => api.sendTestNotification(),
    onSuccess: (res) => setStatus(
      res.ok ? { kind: "ok", detail: res.detail } : { kind: "err", detail: res.detail },
    ),
    onError: (e: { message?: string }) =>
      setStatus({ kind: "err", detail: e?.message ?? "Failed." }),
  });

  if (!isDev) return null;

  return (
    <section className="bg-white border border-slate-200 rounded-lg p-5 space-y-3">
      <div>
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-slate-800">Notifications test</h2>
          <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-700 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5">
            Owner only
          </span>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Sends a test notification to your own email via the same
          pipeline as supervisor alerts. Useful for verifying the
          Resend integration without combing through edge function
          logs. Once push notifications ship, this will also fire a
          push to your registered devices.
        </p>
      </div>

      <div className="flex items-center gap-3">
        <Button
          onClick={() => { setStatus(null); testMut.mutate(); }}
          disabled={testMut.isPending}
        >
          {testMut.isPending ? "Sending…" : "Send test notification"}
        </Button>
        {status && (
          <span
            className={
              "text-xs font-medium " +
              (status.kind === "ok" ? "text-emerald-700" : "text-red-700")
            }
          >
            {status.kind === "ok" ? "✓ " : "✕ "}{status.detail}
          </span>
        )}
      </div>
    </section>
  );
}
